import {Router, RouterLink} from "@angular/router";
import {Component, inject, OnInit} from '@angular/core';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";

import {ToastrService} from "ngx-toastr";
import {NgbActiveModal} from "@ng-bootstrap/ng-bootstrap";

import {appRoutes} from "@numizmat/constants";
import {AlertService} from "@numizmat/services/common";
import {GoogleRegistrationRequest} from "@numizmat/services-models/account";
import {AccessTokenService, CurrentUserStateService, GoogleOAuthApiService} from "@numizmat/services/account";

@Component({
  selector: 'nz-social-registration-modal',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    RouterLink
  ],
  templateUrl: './social-registration-modal.component.html',
  styleUrl: './social-registration-modal.component.scss'
})
export class SocialRegistrationModalComponent implements OnInit {
  private router: Router = inject(Router);
  private alertService: AlertService = inject(AlertService);
  private activeModal: NgbActiveModal = inject(NgbActiveModal);
  private toastrService: ToastrService = inject(ToastrService);
  private accessTokenService: AccessTokenService = inject(AccessTokenService);
  private googleOAuthApiService: GoogleOAuthApiService = inject(GoogleOAuthApiService);
  private currentUserStateService: CurrentUserStateService = inject(CurrentUserStateService);

  protected loading: boolean = false;
  protected errorMessage: string = '';
  protected form: FormGroup = this.initializeForm();

  protected emailInput!: string;
  protected usernameInput!: string;
  protected temporaryTokenInput!: string;

  protected readonly appRoutes = appRoutes;

  public ngOnInit(): void {
    this.username?.setValue(this.usernameInput);
  }

  protected onSubmit(): void {
    this.loading = true;
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      this.loading = false;
      return;
    }
    const formValue = this.form.getRawValue();
    const request: GoogleRegistrationRequest = {
      temporaryToken: this.temporaryTokenInput,
      username: formValue.username,
      password: formValue.password
    };
    this.googleOAuthApiService.registration(request).subscribe({
      next: response => {
        this.loading = false;
        if (response.success) {
          this.accessTokenService.setAccessToken(response.accessToken);
          this.currentUserStateService.setCurrentUser({
            loggedIn: true,
            userData: {
              userId: response.userId,
              username: response.username,
              newMessageCount: 0,
              newNotificationCount: 0,
              confirmationMethod: response.confirmationMethod,
              confirmationMethodName: response.confirmationMethodName
            }
          });
          this.toastrService.success(response.successMessage);
          this.router.navigate([appRoutes.home]).then();
          this.closeModal();
        } else {
          this.errorMessage = response.errorMessage;
        }
      }
    });
  }

  protected closeModal(): void {
    this.activeModal.dismiss();
  }

  protected showUsernameInfo(): void {
    this.alertService.showUsernameInfo();
  }

  private initializeForm(): FormGroup {
    return new FormGroup({
      username: new FormControl(null, [Validators.required, Validators.minLength(2), Validators.maxLength(50)]),
      password: new FormControl(null, [Validators.required, Validators.minLength(8)]),
      confirmPassword: new FormControl(null, [Validators.required, Validators.minLength(8)]),
      isConfirmTermsConditions: new FormControl(false, [Validators.required, Validators.requiredTrue]),
    });
  }

  get username() {
    return this.form.get('username');
  }

  get password() {
    return this.form.get('password');
  }

  get confirmPassword() {
    return this.form.get('confirmPassword');
  }

  get isConfirmTermsConditions() {
    return this.form.get('isConfirmTermsConditions');
  }
}
